const META_PIXEL_ID = (
  import.meta.env?.VITE_META_PIXEL_ID as string | undefined
)?.trim();

const FBEVENTS_SRC = "https://connect.facebook.net/en_US/fbevents.js";

type FbqFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
  push: FbqFunction;
};

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}

let initialized = false;
let lastPageViewKey: string | null = null;

export const isMetaPixelConfigured = (): boolean => Boolean(META_PIXEL_ID);

const ensureFbqStub = (): FbqFunction | null => {
  if (typeof window === "undefined") return null;

  if (window.fbq) return window.fbq;

  const fbq: FbqFunction = function (...args: unknown[]) {
    if (typeof fbq.callMethod === "function") {
      fbq.callMethod(...args);
      return;
    }
    fbq.queue.push(args);
  } as FbqFunction;

  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.push = fbq;

  window.fbq = fbq;
  if (!window._fbq) {
    window._fbq = fbq;
  }

  return fbq;
};

const loadFbeventsScript = (): void => {
  if (typeof document === "undefined") return;
  if (document.querySelector(`script[src="${FBEVENTS_SRC}"]`)) return;

  const script = document.createElement("script");
  script.async = true;
  script.src = FBEVENTS_SRC;
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
};

/**
 * Initialise Meta Pixel once. Safe to call multiple times — subsequent calls
 * are no-ops. When `VITE_META_PIXEL_ID` is absent the SDK is never loaded and
 * all track calls silently no-op.
 */
export const initMetaPixel = (): void => {
  if (initialized) return;
  initialized = true;

  if (!META_PIXEL_ID) {
    if (import.meta.env?.DEV) {
      console.info(
        "[Meta Pixel] VITE_META_PIXEL_ID not set — pixel tracking disabled.",
      );
    }
    return;
  }

  const fbq = ensureFbqStub();
  if (!fbq) return;

  loadFbeventsScript();
  fbq("init", META_PIXEL_ID);
  // Initial PageView; SPA navigations call trackMetaPageView separately.
  lastPageViewKey =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : null;
  fbq("track", "PageView");
};

/**
 * Thin wrapper around `fbq('track', ...)`.
 * If Meta Pixel was not initialised (missing ID), the call is a silent no-op.
 */
export const trackMetaEvent = (
  event: string,
  parameters?: Record<string, unknown>,
): void => {
  if (!META_PIXEL_ID) return;

  initMetaPixel();

  const fbq = typeof window !== "undefined" ? window.fbq : undefined;
  if (!fbq) return;

  if (parameters && Object.keys(parameters).length > 0) {
    fbq("track", event, parameters);
    return;
  }

  fbq("track", event);
};

/**
 * Fire a Meta `PageView` for the current (or provided) location. Dedupes
 * identical path+search keys so init + first SPA effect do not double-count.
 */
export const trackMetaPageView = (locationKey?: string): void => {
  if (!META_PIXEL_ID) return;

  if (!initialized) {
    initMetaPixel();
  }

  const key =
    locationKey ??
    (typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "");

  if (key && key === lastPageViewKey) return;
  lastPageViewKey = key || null;

  const fbq = typeof window !== "undefined" ? window.fbq : undefined;
  if (!fbq) return;
  fbq("track", "PageView");
};